import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  Typography,
  IconButton,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { dateToTimeDateString } from '../../../helpers/dates';
import { getTaskItemTitle } from '../../../helpers/event-helper';
import * as userHelper from '../../../helpers/user-helper';

const useStyles = makeStyles(theme => ({
  addItemDialog: {
    [`& .MuiDialog-paper`]: {
      margin: 0,
      width: '95vw',
      height: props =>
        props.customDialogHeight ? `${props.customDialogHeight}px` : 'initial',
    },
  },
  dialogTitle: {
    padding: theme.spacing(2, 1),
    paddingBottom: 0,
  },
  dialogContent: {
    overflow: 'hidden',
    padding: theme.spacing(1, 1),
    paddingTop: theme.spacing(2),
  },
  itemDialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
  },
  itemCheckboxContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    overflow: 'hidden',
  },
  itemCheckbox: {
    padding: 0,
    marginRight: theme.spacing(1),
  },
  checkboxTextContainer: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeItemButton: {
    padding: 0,
  },
  taskDetails: {
    display: 'block',
    lineHeight: 1,
    textAlign: 'right',
  },
  itemDialogHeaderActions: {
    display: 'flex',
    alignItems: 'center',
  },
  itemMetadata: {
    textAlign: 'right',
    marginRight: theme.spacing(2),
    minWidth: '100px',
  },
}));

const minKeyboardHeight = 300;

export const AddItemDialog = ({
  isOpen,
  onItemAdd,
  onItemEdit,
  onClose,
  item,
  onDelete,
  mode,
}) => {
  const [customDialogHeight, setCustomDialogHeight] = useState(null);

  const classes = useStyles({ customDialogHeight });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [itemDescription, setItemDescription] = useState('');
  const [isItemCompleted, setIsItemCompleted] = useState(false);
  const [customMaxRows, setCustomMaxRows] = useState(18);

  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const shrinkModal = useCallback(() => {
    if (isSmallScreen) {
      setCustomDialogHeight(window.innerHeight * 0.65);
      setCustomMaxRows(11);
    }
  }, [isSmallScreen]);

  const restoreModalSize = useCallback(() => {
    setCustomMaxRows(18);
    setCustomDialogHeight(null);
  }, []);

  useEffect(() => {
    let listener;

    if (window.visualViewport == null) {
      return;
    }

    listener = () => {
      const newState =
        window.screen.height - minKeyboardHeight > window.visualViewport.height;
      setIsKeyboardOpen(newState);
    };

    window.visualViewport.addEventListener('resize', listener);

    return () => {
      if (!listener) {
        return;
      }
      window.visualViewport.removeEventListener('resize', listener);
    };
  }, []);

  useEffect(() => {
    if (isKeyboardOpen) {
      shrinkModal();
    } else {
      restoreModalSize();
    }
  }, [isKeyboardOpen, isSmallScreen, restoreModalSize, shrinkModal]);

  useEffect(() => {
    if (item) {
      setItemDescription(item.description ?? '');
      setIsItemCompleted(item.isCompleted);
    }
  }, [item]);

  const handleSaveClick = () => {
    const trimmedDescription = itemDescription.trim();

    if (item) {
      onItemEdit(trimmedDescription, item._id, isItemCompleted);
    } else {
      onItemAdd(trimmedDescription, isItemCompleted);
    }

    setItemDescription('');
    onClose();
  };

  const handleDescriptionChange = event => {
    let newDescriptionValue = event.target.value;

    setItemDescription(newDescriptionValue);
  };

  return (
    <Dialog className={classes.addItemDialog} fullWidth open={isOpen}>
      <DialogTitle className={classes.dialogTitle}>
        <div className={classes.itemDialogHeader}>
          <div className={classes.itemCheckboxContainer}>
            {mode === 'task' && (
              <Checkbox
                color="primary"
                className={classes.itemCheckbox}
                style={
                  !isItemCompleted ? { color: theme.palette.error.main } : {}
                }
                disabled={!itemDescription}
                onClick={() => {
                  setIsItemCompleted(!isItemCompleted);
                }}
                checked={isItemCompleted}
              />
            )}
            <Typography className={classes.checkboxTextContainer}>
              {getTaskItemTitle(itemDescription) || 'Untitled item'}
            </Typography>
          </div>

          {item && (
            <div className={classes.itemDialogHeaderActions}>
              <div className={classes.itemMetadata}>
                <Typography className={classes.taskDetails} variant="caption">
                  {userHelper.fullName(item.createdBy)}
                </Typography>
                <Typography variant="caption">
                  {`${dateToTimeDateString(item.createdAt)}`}
                </Typography>
              </div>
              <IconButton
                className={classes.removeItemButton}
                onClick={onDelete}
                size="large"
              >
                <DeleteIcon />
              </IconButton>
            </div>
          )}
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.dialogContent}>
        <TextField
          name="itemDescription"
          className={classes.textField}
          multiline
          fullWidth
          minRows={customMaxRows}
          maxRows={customMaxRows}
          label="Item description"
          variant="outlined"
          onChange={handleDescriptionChange}
          value={itemDescription}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          size="small"
        >
          Close
        </Button>
        <Button
          onClick={handleSaveClick}
          disabled={!getTaskItemTitle(itemDescription)}
          variant="contained"
          color="primary"
          size="small"
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
